import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { resizeImage } from '../../api/cloudinary';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import { DbLeagueTeam } from '@par/shared/types/league';
import { SearchPlayerDialog } from '../../dialog/SearchPlayerDialog';
import { LeagueAddTeamDialog } from '../../dialog/LeagueAddTeamDialog';
import { StyledPaperItem } from '../../common/mui-components';
import { LeagueAddMatchScheduleDialog } from '../../dialog/LeaguAddMatchScheduleDialog';
import { LeagueAddWeekScheduleDialog } from '../../dialog/LeaguAddWeekScheduleDialog';

type Member = {
  _id: string;
  avatarPublicId: string;
  username: string;
};

type PropTypes = {
  profileId: string;
};

export const LeagueAdmin = ({ profileId }: PropTypes) => {
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));
  const [teams, setTeams] = useState<DbLeagueTeam[]>();
  const [sortedTeams, setSortedTeams] = useState<DbLeagueTeam[]>();
  const [members, setMembers] = useState<Member[]>();
  const [selectedTeam, setSelectedTeam] = useState<DbLeagueTeam>();
  const [openSearchPlayersDialog, setOpenSearchPlayersDialog] = useState(false);
  const [openAddTeamDialog, setOpenAddTeamDialog] = useState(false);
  const [openAddMatchScheduleDialog, setOpenAddMatchScheduleDialog] =
    useState(false);
  const [openAddWeekScheduleDialog, setOpenAddWeekScheduleDialog] =
    useState(false);

  useEffect(() => {
    fetchAllTeams();
  }, []);

  useEffect(() => {
    if (!teams) return;
    const sorted = teams.sort((a, b) => a.teamNumber - b.teamNumber);
    setSortedTeams(sorted);
  }, [teams]);

  const fetchAllTeams = async () => {
    const res = await fetchGet(`/league/teams/all`);
    setTeams(res.teams);
    setMembers(res.members);
    console.log('fetchAllTeams', res);
  };

  const handleAddMemberClick = (selectedTeam: DbLeagueTeam) => {
    setSelectedTeam(selectedTeam);
    setOpenSearchPlayersDialog(true);
  };

  const handlePlayerClick = (profileId: string) => {
    addMember(profileId);
  };

  const addMember = async (memberProfileId: string) => {
    if (!selectedTeam) return;
    const res = await fetchPost(`/league/add-member`, {
      profileId,
      teamId: selectedTeam._id,
      memberProfileId,
    });
    const updatedTeam = res.updatedTeam;
    const filteredMembers = members.filter(
      (m) => !updatedTeam.members.includes(m._id)
    );
    const updatedMembers = filteredMembers.concat(res.members);
    setMembers(updatedMembers);
    const filteredTeams = teams.filter((t) => t._id !== selectedTeam._id);
    const updatedTeams = filteredTeams.concat(updatedTeam);
    setTeams(updatedTeams);
    setSelectedTeam(updatedTeam);
    console.log('addNewMember', res);
  };

  const handleRemovePlayerClick = async (
    team: DbLeagueTeam,
    profileId: string
  ) => {
    await removeMember(team, profileId);
  };

  const removeMember = async (team: DbLeagueTeam, memberProfileId: string) => {
    const res = await fetchPost(`/league/remove-member`, {
      profileId,
      teamId: team._id,
      memberProfileId,
    });
    const updatedTeam = res.updatedTeam;
    const filteredMembers = members.filter(
      (m) => !updatedTeam.members.includes(m._id)
    );
    const updatedMembers = filteredMembers.concat(res.members);
    setMembers(updatedMembers);
    const filteredTeams = teams.filter((t) => t._id !== team._id);
    const updatedTeams = filteredTeams.concat(updatedTeam);
    setTeams(updatedTeams);
    console.log('removeMember', res);
  };

  const handleAddTeamClick = async (teamName: string) => {
    setOpenAddTeamDialog(false);
    await addTeam(teamName);
  };

  const addTeam = async (teamName: string) => {
    const res = await fetchPost(`/league/add-team`, {
      profileId,
      teamName,
    });
    const newTeam = res.newTeam;
    const updatedTeams = teams.concat(newTeam);
    setTeams(updatedTeams);
    console.log('addTeam', res);
  };

  const handleRemoveTeamClick = async (teamId: string) => {
    const res = await fetchPost(`/league/remove-team`, {
      profileId,
      teamId,
    });
    const updatedTeams = teams.filter((t) => t._id !== teamId);
    setTeams(updatedTeams);
    console.log('handleRemoveTeamClick', res);
  };

  const userDisplay = (id: string) => {
    if (!members) return <></>;
    const member = members.find((m) => m._id === id);
    return (
      <Stack direction={'row'} spacing={1} width={'100%'} alignItems={'center'}>
        <Avatar
          src={resizeImage(member.avatarPublicId, 80, 80)}
          variant="rounded"
          sx={{
            height: 25,
            width: 25,
          }}
        />
        <Typography
          fontWeight={600}
          textAlign={'left'}
          width="100%"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'pre',
          }}
        >
          {member.username}{' '}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      {selectedTeam ? (
        <SearchPlayerDialog
          close={() => setOpenSearchPlayersDialog(false)}
          handlePlayerClick={handlePlayerClick}
          members={selectedTeam.members}
          open={openSearchPlayersDialog}
        />
      ) : null}
      <LeagueAddTeamDialog
        close={() => setOpenAddTeamDialog(false)}
        handleAddTeamClick={handleAddTeamClick}
        open={openAddTeamDialog}
      />
      {teams ? (
        <LeagueAddMatchScheduleDialog
          close={() => setOpenAddMatchScheduleDialog(false)}
          open={openAddMatchScheduleDialog}
          profileId={profileId}
          teams={teams}
        />
      ) : null}
      {teams ? (
        <LeagueAddWeekScheduleDialog
          close={() => setOpenAddWeekScheduleDialog(false)}
          open={openAddWeekScheduleDialog}
          profileId={profileId}
        />
      ) : null}
      <Container>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => setOpenAddTeamDialog(true)}
            >
              Add New Team
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenAddMatchScheduleDialog(true)}
            >
              Add New Match Schedule
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenAddWeekScheduleDialog(true)}
            >
              Add New Week Schedule
            </Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid item sm={12} md={12}>
              <StyledPaperItem></StyledPaperItem>
            </Grid>
            <Grid item sm={12} md={12}>
              <StyledPaperItem>
                <Grid container spacing={2}>
                  {sortedTeams?.map((t) => (
                    <Grid item sm={12} md={3}>
                      <Stack spacing={2}>
                        <Box position="relative">
                          <Typography
                            variant="h6led"
                            textAlign={'center'}
                            sx={{ color: '#00e0ff' }}
                          >
                            {t.teamName}
                          </Typography>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveTeamClick(t._id)}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 5,
                            }}
                          >
                            <ClearIcon color="error" />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            bgcolor: '#131d22',
                          }}
                        >
                          <List component="nav" disablePadding>
                            {t.members.map((id) => (
                              <>
                                <ListItem
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      onClick={() =>
                                        handleRemovePlayerClick(t, id)
                                      }
                                    >
                                      <ClearIcon color="error" />
                                    </IconButton>
                                  }
                                  disablePadding
                                >
                                  <ListItemButton>
                                    <ListItemText
                                      primary={userDisplay(id)}
                                      sx={{ textAlign: 'center' }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                                <Divider />
                              </>
                            ))}
                            <ListItem disablePadding>
                              <ListItemButton
                                onClick={() => handleAddMemberClick(t)}
                              >
                                <ListItemText
                                  primary="Add Member"
                                  sx={{ textAlign: 'center' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          </List>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </StyledPaperItem>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </>
  );
};
